import { Box, ButtonBase, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { Chevron } from "@/components/Chevron";
import { RegistrationContext } from "@/components/property_registration/RegistrationContext";
import { layoutTokens, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";

const screens: Record<string, { title: string; heading?: string; description?: string; back?: string }> = {
  経費情報: {
    title: "毎月の経費",
    heading: "毎月の経費の確認・編集",
    description: "金額が変わった月があれば、新しい金額を追加できます。以前の日付・金額はそのまま記録として残ります。",
  },
  一棟物件経費情報: {
    title: "毎月の経費",
    heading: "毎月の経費の確認・編集",
    description: "建物の経費と、年に一度・不定期の支出を確認できます。",
  },
  借入情報: {
    title: "借入",
    heading: "借入の確認・編集",
    description:
      "利率が変わった月や、繰上返済をした月があれば追加できます。以前の日付・金額はそのまま記録として残ります。",
  },
  保険情報: {
    title: "保険",
    heading: "保険の確認・編集",
    description: "保険を更新したら、新しい期間と保険料を追加できます。以前の期間・保険料はそのまま記録として残ります。",
  },
  賃貸契約情報: {
    title: "賃貸契約",
    heading: "賃貸契約の確認・編集",
    description: "契約を更新したり、新しい入居者と契約したら追加できます。以前の契約はそのまま記録として残ります。",
  },
  物件詳細編集: {
    title: "物件の基本情報",
    heading: "物件の基本情報の確認・編集",
    description: "直した内容は保存するとすぐ反映されます。",
  },
  購入時情報編集: {
    title: "購入時の情報",
    heading: "購入時の情報の確認・編集",
    description: "売買契約書と決済時の明細をもとに直せます。利回りや売却時の手残りの計算に使います。",
  },
  部屋情報登録: {
    title: "部屋を追加",
    heading: "部屋を追加",
    description: "家賃と契約期間を入力すると、収支と部屋別の状況に反映されます。",
  },
  部屋情報編集: {
    title: "部屋の編集",
    heading: "部屋の確認・編集",
    description: "直した内容は保存するとすぐ反映されます。",
  },
  契約の追加: {
    title: "契約の追加",
    heading: "次の契約の内容",
    description: "契約期間と、家賃・契約開始時の収入と支出を入力してください。",
    back: "賃貸契約",
  },
};

const useStyles = makeStyles(() => ({
  wrap: {
    width: "100%",
    maxWidth: 560,
    margin: "0 auto",
    [theme.breakpoints.up("md")]: { marginTop: layoutTokens.space[32], marginBottom: layoutTokens.space[64] },
  },
  pageTitle: { ...textStyles["PC/Heading/H2"], color: theme.palette.semantic["text/heading"], margin: "16px 0" },
  card: {
    padding: "24px 24px 32px",
    backgroundColor: theme.palette.semantic["surface/card"],
    minHeight: "calc(100vh - 56px)",
    [theme.breakpoints.up("md")]: { minHeight: 0, padding: "56px 40px 64px", borderRadius: layoutTokens.radius.lg },
  },
  heading: { ...textStyles["SP/Heading/H2"], overflowWrap: "anywhere" },
  description: {
    ...textStyles["SP/Body/Small"],
    color: theme.palette.text.secondary,
    marginTop: layoutTokens.space[8],
  },
  content: {
    "& h4": { ...textStyles["SP/Heading/H4"] },
    "& [data-edit-section]": { marginTop: 32, marginBottom: 8, ...textStyles["SP/Heading/H4"] },
    "& [data-edit-section]:first-child": { marginTop: 0 },
    "& [data-edit-form-panel]": { background: "none", padding: 0 },
    "& [data-edit-cost-inputs]": { display: "flex", flexDirection: "column" },
    "& [data-edit-cost-inputs] > [data-edit-field=month]": { order: 1 },
    "& [data-edit-form-panel] button[type=submit]": { marginTop: 0 },
    "& [data-registration-field]": { marginBottom: 0 },

    "& [data-edit-grid]": { width: "100%", margin: 0 },
    "& [data-edit-grid] > .MuiGrid-item": { maxWidth: "100%", flexBasis: "100%", padding: 0 },
    "& .MuiTypography-body2": { fontSize: 12 },
    "& [data-edit-form-actions]": {
      marginTop: 32,
      marginLeft: 0,
      marginRight: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
      width: "100%",
    },
    "& [data-edit-form-actions] > .MuiGrid-item": { maxWidth: "100%", width: "100%", flexBasis: "auto", padding: 0 },
    "& [data-edit-form-actions] > .MuiGrid-item:first-child": { order: 1 },
    "& [data-edit-form-actions] > .MuiGrid-item:last-child": { order: 0 },
    "& button[type=submit]": { width: "100%", marginTop: 32 },
    [theme.breakpoints.up("md")]: {
      "& [data-edit-form-actions] button[data-button-tone=primary]": {
        maxWidth: 360,
        marginLeft: "auto",
        marginRight: "auto",
      },
      "& button[type=submit]": { maxWidth: 360, display: "flex", marginLeft: "auto", marginRight: "auto" },
    },
  },
}));

export const PropertyEditLayout: React.FC<{
  headerTitle: string;
  onBack?: () => void;
  isBackGroundWhite?: boolean;
  heading?: string;
  description?: string;
  backLabel?: string;
}> = ({ headerTitle, onBack, children, heading, description, backLabel }) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const screen = screens[headerTitle] || { title: headerTitle, back: "戻る" };
  const bodyHeading = heading || screen.heading;
  return (
    <BaseTemplate
      headerTitle={isMobile ? screen.title : undefined}
      onBack={isMobile ? onBack : undefined}
      isDense
      isPcV2
      disableBottomPadding
    >
      <Box className={classes.wrap}>
        {!isMobile && (
          <>
            <ButtonBase onClick={onBack} style={{ gap: 4, fontSize: 14, color: theme.palette.primary.main }}>
              <Chevron direction="back" />
              {backLabel || screen.back || "物件詳細"}
            </ButtonBase>
            <Typography component="h1" className={classes.pageTitle}>
              {screen.title}
            </Typography>
          </>
        )}
        <RegistrationContext.Provider value={true}>
          <Box className={classes.card}>
            {bodyHeading && (
              <Box mb={4}>
                <Typography component="h2" className={classes.heading}>
                  {bodyHeading}
                </Typography>
                {(description || screen.description) && (
                  <Typography className={classes.description}>{description || screen.description}</Typography>
                )}
              </Box>
            )}
            <Box className={classes.content}>{children}</Box>
          </Box>
        </RegistrationContext.Provider>
      </Box>
    </BaseTemplate>
  );
};
